import type { Flow } from "../../lib/flow.js";
import { formatUnit } from "../../lib/format.js";

/**
 * The text equivalent, rendered rather than hidden.
 *
 * docs/REQUIREMENTS.md section 4 requires direction and magnitude to be
 * carried by more than colour and animation. This is where magnitude reaches
 * anyone who cannot compare two stroke widths, and it stays a real `<ul>` with
 * a name because the browser suite reaches it as
 * `getByRole("list", { name: "Power flow summary" })` in eight places.
 * Restyling it as a grid of divs would take the list role away and silently
 * break all eight.
 */
export function FlowLegend({ flows }: { flows: Flow[] }) {
  return (
    <ul aria-label="Power flow summary" className="mt-4 flex flex-col gap-2">
      {flows.map((flow) => (
        <li
          key={flow.id}
          className="flex items-center justify-between gap-4 border-b border-line pb-2 last:border-0 last:pb-0"
        >
          <span className="flex items-center gap-2 text-sm text-ink">
            <Arrow flow={flow} />
            {flow.description}
          </span>
          <span className="tabular text-sm font-medium text-ink">
            {formatUnit(flow.watts, "W")}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * A character, not an icon: it survives a stylesheet that did not load.
 * `aria-hidden` because the words beside it already say which way the power is
 * going, and "right arrow charging" is noise.
 */
function Arrow({ flow }: { flow: Flow }) {
  const glyph = flow.from === null ? "•" : flow.to === "battery" ? "↓" : "→";
  return (
    <span aria-hidden="true" className="text-accent-2-ink">
      {glyph}
    </span>
  );
}
